import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { applySeo } from '@/lib/seo';

export default function PolitiqueConfidentialite() {
  useEffect(() => {
    applySeo({
      title: 'Politique de confidentialité | Easy Car',
      description: "Politique de confidentialité du site Easy Car : données personnelles, sécurité, cookies, services externes et droits des utilisateurs.",
      canonical: 'https://easycar.re/politique-confidentialite',
    });

    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-3xl mx-auto">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            Politique de confidentialité
          </h1>
          <p className="text-foreground-500 text-xs md:text-sm mt-2">
            Dernière mise à jour : 24 septembre 2026
          </p>

          <div className="bg-white rounded-lg border border-background-200 p-5 md:p-10 mt-6 md:mt-8">
            {/* 1. Responsable du traitement */}
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                1. Responsable du traitement
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>Le site easycar.re est édité par :</p>
                <div className="space-y-0.5">
                  <p className="font-heading font-semibold text-foreground-900">CM MOTORS – EASY CAR</p>
                  <p>57 Rue du Général de Gaulle</p>
                  <p>97430 Le Tampon</p>
                  <p>La Réunion – France</p>
                </div>
                <div className="space-y-0.5">
                  <p>
                    E-mail :{' '}
                    <a
                      href="mailto:easycar974@gmail.com"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      easycar974@gmail.com
                    </a>
                  </p>
                  <p>
                    Téléphone :{' '}
                    <a
                      href="tel:+262692505551"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      0692 50 55 51
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Données collectées directement par le site */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                2. Données collectées directement par le site
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>
                  Le site public Easy Car ne comporte aucun formulaire de contact et ne collecte pas
                  directement de données personnelles à des fins commerciales, publicitaires ou
                  marketing.
                </p>
                <p>Aucun compte client n'est proposé sur la partie publique du site.</p>
                <p>
                  Easy Car n'utilise pas les données de navigation des visiteurs à des fins de profilage
                  ou de publicité ciblée.
                </p>
              </div>
            </section>

            {/* 3. Données techniques et sécurité */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                3. Données techniques et sécurité
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>
                  Comme pour tout site internet, certaines données techniques peuvent être traitées
                  automatiquement lors de la consultation du site afin d'en assurer le fonctionnement, la
                  sécurité et la protection contre les accès ou comportements malveillants.
                </p>
                <p>Ces données peuvent notamment comprendre :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>l'adresse IP ;</li>
                  <li>la date et l'heure de la connexion ;</li>
                  <li>les informations techniques relatives au navigateur ou au terminal ;</li>
                  <li>
                    les journaux techniques nécessaires au fonctionnement et à la sécurité du serveur.
                  </li>
                </ul>
                <p>
                  Ces traitements ont pour finalité le bon fonctionnement et la sécurisation du site.
                </p>
                <p>
                  Lorsqu'un traitement de données personnelles est nécessaire à ces finalités, il repose
                  sur l'intérêt légitime de CM MOTORS à assurer la sécurité et le bon fonctionnement de
                  son site internet.
                </p>
                <p>
                  Les données techniques sont accessibles uniquement aux personnes habilitées et,
                  lorsque cela est nécessaire, à l'hébergeur LWS dans le cadre de la fourniture de ses
                  services.
                </p>
                <p>
                  Elles sont conservées uniquement pendant la durée nécessaire au fonctionnement, à la
                  sécurité du site et au respect des éventuelles obligations légales ou techniques
                  applicables.
                </p>
              </div>
            </section>

            {/* 4. Cookies */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                4. Cookies
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>
                  Le site public Easy Car n'utilise aucun cookie publicitaire, cookie de mesure
                  d'audience, cookie marketing ou outil de profilage.
                </p>
                <p>Aucun Google Analytics, pixel Meta ou autre outil publicitaire n'est installé.</p>
                <p>
                  Par conséquent, aucun bandeau de consentement aux cookies n'est affiché sur le site
                  public tant qu'aucun cookie ou traceur soumis au consentement n'est utilisé.
                </p>
                <p>
                  L'espace d'administration privé du site peut utiliser un cookie technique strictement
                  nécessaire à l'authentification et à la sécurité de l'administrateur.
                </p>
                <p>
                  Ce cookie technique n'est utilisé ni à des fins publicitaires ni à des fins de suivi
                  des visiteurs du site public.
                </p>
                <p>
                  Si des technologies nécessitant le consentement de l'utilisateur sont ajoutées
                  ultérieurement, la présente politique de confidentialité et le dispositif de gestion
                  des cookies devront être adaptés avant leur mise en service.
                </p>
              </div>
            </section>

            {/* 5. Liens et services externes */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                5. Liens et services externes
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>Le site contient des liens permettant notamment :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>d'appeler Easy Car ;</li>
                  <li>d'envoyer un e-mail ;</li>
                  <li>de contacter Easy Car via WhatsApp ;</li>
                  <li>d'accéder à Facebook ;</li>
                  <li>d'ouvrir un itinéraire dans Google Maps.</li>
                </ul>
                <p>
                  Ces services ne sont pas intégrés au site sous la forme d'outils de suivi ou de
                  contenus embarqués.
                </p>
                <p>
                  Lorsque l'utilisateur choisit d'accéder à l'un de ces services externes, il quitte le
                  site Easy Car ou utilise un service exploité par un tiers.
                </p>
                <p>
                  Les données éventuellement traitées par WhatsApp, Meta/Facebook, Google ou tout autre
                  service externe sont alors soumises aux politiques de confidentialité propres à ces
                  services.
                </p>
              </div>
            </section>

            {/* 6. Droits des utilisateurs */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                6. Droits des utilisateurs
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>
                  Conformément à la réglementation applicable en matière de protection des données
                  personnelles, toute personne concernée dispose, selon la nature du traitement, de
                  droits concernant ses données personnelles, notamment :
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>droit d'accès ;</li>
                  <li>droit de rectification ;</li>
                  <li>droit à l'effacement ;</li>
                  <li>droit à la limitation du traitement ;</li>
                  <li>droit d'opposition lorsque celui-ci est applicable.</li>
                </ul>
                <p>
                  Pour exercer ses droits ou obtenir des informations concernant le traitement de ses
                  données personnelles, l'utilisateur peut contacter Easy Car à :{' '}
                  <a
                    href="mailto:easycar974@gmail.com"
                    className="text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    easycar974@gmail.com
                  </a>
                </p>
              </div>
            </section>

            {/* 7. Réclamation auprès de la CNIL */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                7. Réclamation auprès de la CNIL
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>
                  Si une personne estime, après avoir contacté Easy Car, que ses droits concernant ses
                  données personnelles ne sont pas respectés, elle peut introduire une réclamation
                  auprès de la Commission nationale de l'informatique et des libertés (CNIL).
                </p>
                <p>
                  Site internet :{' '}
                  <a
                    href="https://www.cnil.fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    https://www.cnil.fr
                  </a>
                </p>
              </div>
            </section>

            {/* 8. Hébergement */}
            <section className="mt-8 pt-8 border-t border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                8. Hébergement
              </h2>
              <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                <p>Le site easycar.re est hébergé par :</p>
                <div className="space-y-0.5">
                  <p className="font-heading font-semibold text-foreground-900">LWS – Ligne Web Services</p>
                  <p>10 rue Penthièvre</p>
                  <p>75008 Paris</p>
                  <p>France</p>
                </div>
                <div className="space-y-0.5">
                  <p>
                    Téléphone :{' '}
                    <a
                      href="tel:+33177623003"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      01 77 62 30 03
                    </a>
                  </p>
                  <p>
                    Site internet :{' '}
                    <a
                      href="https://www.lws.fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      https://www.lws.fr
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-600 font-medium text-sm hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}